import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { ChartConfiguration } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { StatisticsData } from '../types';
import { useTranslation } from 'react-i18next';

interface PurposeTableProps {
    data?: StatisticsData[];
}

Chart.register(ChartDataLabels);

const CHART_COLORS = [
    '#10B981',  // Зеленый
    '#3B82F6',  // Синий
    '#F59E0B',  // Оранжевый
    '#94A3B8',  // Серый
    '#EC4899',  // Розовый
    '#8B5CF6',  // Фиолетовый
    '#F43F5E',  // Красный
    '#14B8A6'   // Бирюзовый
];


export const PurposeTable: React.FC<PurposeTableProps> = ({ data }) => {
    const { t } = useTranslation();
    const chartRefs = useRef<{ [key: string]: HTMLCanvasElement | null }>({});
    const chartInstances = useRef<{ [key: string]: Chart | null }>({});

    const createCenterTextPlugin = (year: string) => ({
        id: 'centerText',
        afterDraw(chart: Chart) {
            const { ctx } = chart;
            const chartArea = chart.chartArea;
            ctx.save();
            ctx.font = 'bold 24px Arial';
            ctx.fillStyle = '#000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const centerX = (chartArea.left + chartArea.right) / 2;
            const centerY = (chartArea.top + chartArea.bottom) / 2;
            ctx.fillText(`${year} год`, centerX, centerY);
            ctx.restore();
        }
    });

    const createChartConfig = (year: string, yearData?: StatisticsData): ChartConfiguration<'doughnut'> => {
        const purposeData = yearData?.purposeStatistics || {};
        const labels = Object.keys(purposeData);
        const values = Object.values(purposeData);
        const total = values.reduce((sum, value) => sum + value, 0);
        const percentages = values.map(value => ((value / total) * 100).toFixed(1));

        return {
            type: 'doughnut',
            plugins: [createCenterTextPlugin(year)],
            data: {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: CHART_COLORS.slice(0, values.length),
                    borderWidth: 0,
                    spacing: 0,
                    hoverOffset: 4
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '60%',
                plugins: {
                    datalabels: {
                        color: '#fff',
                        font: {
                            size: 16,
                            weight: 'bold',
                            family: 'Arial'
                        },
                        formatter: (_, context) => {
                            return percentages[context.dataIndex] + '%';
                        }
                    },
                    legend: {
                        display: false
                    },
                    title: {
                        display: false
                    }
                },
                layout: {
                    padding: {
                        top: 20,
                        bottom: 20,
                        left: 20,
                        right: 20
                    }
                },
                elements: {
                    arc: {
                        borderWidth: 0
                    }
                }
            }
        };
    };

    useEffect(() => {
        if (!data || data.length === 0) return;


        Object.values(chartInstances.current).forEach(chart => {
            chart?.destroy();
        });
        chartInstances.current = {};


        data.forEach((yearData) => {
            const canvasRef = chartRefs.current[`chart${yearData.year}`];
            if (canvasRef) {
                const ctx = canvasRef.getContext('2d');
                if (ctx) {
                    chartInstances.current[`chart${yearData.year}`] = new Chart(
                        ctx,
                        createChartConfig(yearData.year.toString(), yearData)
                    );
                }
            }
        });

        return () => {
            Object.values(chartInstances.current).forEach(chart => chart?.destroy());
        };
    }, [data]);

    if (!data || data.length === 0) {
        return null;
    }


    const latestData = data[data.length - 1];
    const purposeData = latestData.purposeStatistics;
    const labels = Object.keys(purposeData);
    const values = Object.values(purposeData);

    const ChartContainer = ({ yearData }: { yearData: StatisticsData }) => (
        <div style={{ 
            width: '100%',
            maxWidth: '400px',
            background: 'rgba(249, 250, 251, 0.5)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            height: '400px'
       
        }}>
            <div style={{ flex: 1 }}>
                <canvas 
                    ref={el => chartRefs.current[`chart${yearData.year}`] = el}
                    style={{ width: '100%', height: '100%' }}
                />
            </div>
            <div style={{
                marginTop: '20px',
                borderTop: '1px solid #E5E7EB',
                paddingTop: '15px'
            }}>
                {Object.entries(yearData.purposeStatistics).map(([label, value], index) => (
                    <div key={label} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        fontSize: '14px',
                        color: '#374151'
                    }}>
                        <span>{label}</span>
                        <span style={{ color: '#6B7280' }}>{value} объектов</span>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div style={{
            width: '100%',
            maxWidth: '2000px',
            margin: '20px auto',
            padding: '30px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <h1 style={{
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '25px',
                textAlign: 'center',
                color: '#1F2937',
                letterSpacing: '0.025em',
                position: 'relative'
            }}>
                {t('Distribution of property by purpose')}
            </h1>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '16px',
                marginBottom: '20px',
                padding: '0 20px'
            }}>
                {labels.map((label, index) => (
                    <div key={label} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        background: 'rgba(249, 250, 251, 0.95)',
                        padding: '8px 16px',
                        borderRadius: '30px',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        border: '1px solid rgba(229, 231, 235, 0.5)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                            boxShadow: `0 0 10px ${CHART_COLORS[index % CHART_COLORS.length]}40`
                        }} />
                        <span style={{ 
                            fontSize: '14px', 
                            color: '#374151',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                        }}>{label}</span>
                    </div>
                ))}
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                flexWrap: 'wrap',
                padding: '0 10px'
            }}>
                {data.map((yearData) => (
                    <ChartContainer key={yearData.year} yearData={yearData} />
                ))}
            </div>
        </div>
    );
};